import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Pause } from "lucide-react";
import { mediumArt, api } from "@/lib/api";
import { usePlayer } from "@/contexts/PlayerContext";
import { gradientCssForArtist } from "@/lib/artistTheme";
import FireBadge from "@/components/FireBadge";
import SmartImage from "@/components/SmartImage";

export const SongCard = ({ song, queue }) => {
  const { current, playing, togglePlay, playTracks } = usePlayer();
  const isCurrent = current?.trackId === song.trackId;
  const isPlayingThis = isCurrent && playing;

  const onPlay = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isCurrent) togglePlay();
    else {
      const list = queue || [song];
      const idx = Math.max(0, list.findIndex((t) => t.trackId === song.trackId));
      playTracks(list, idx);
    }
  };

  return (
    <Link
      to={song.collectionId ? `/album/${song.collectionId}` : "#"}
      data-testid={`song-card-${song.trackId}`}
      className="group relative flex flex-col gap-3 p-3 rounded-xl bg-white/[0.02] hover:bg-white/[0.06] border border-transparent hover:border-white/5 transition-all"
    >
      <div className="relative w-full aspect-square rounded-md overflow-hidden shadow-lg">
        <SmartImage
          src={mediumArt(song.artworkUrl100)}
          alt={song.trackName || ""}
          seed={song.trackName || song.artistName || "?"}
          className="absolute inset-0 w-full h-full"
          imgClassName="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          tileStyle={{ borderRadius: 0 }}
        />
        <button
          onClick={onPlay}
          data-testid={`song-card-play-${song.trackId}`}
          className="absolute bottom-2 right-2 w-11 h-11 rounded-full bg-[#FF2E00] text-white flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 shadow-[0_8px_24px_rgba(255,51,102,0.4)]"
        >
          {isPlayingThis ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" className="ml-0.5" />}
        </button>
        {/* 🔥 emoji on tracks that are doing numbers */}
        <div className="absolute top-2 left-2">
          <FireBadge item={song} size="md" />
        </div>
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-sm font-medium truncate flex-1">{song.trackName}</p>
        </div>
        <p className="text-xs text-white/50 truncate mt-0.5">{song.artistName}</p>
      </div>
    </Link>
  );
};

export const AlbumCard = ({ album }) => {
  // Apple Music shows a "NEW" tag on albums released within the last
  // ~7 weeks (≈ 50 days). We use 60 days here to stay generous given
  // independent releases trickle in over longer windows. We ALSO carve
  // out a tighter "OUT NOW" window (≤14 days) that's louder and more
  // urgent — it's the same data path Apple Music uses to surface
  // brand-new drops above the "NEW" tier. Both are computed inline so
  // every surface that renders AlbumCard gets them for free.
  const { isNew, isOutNow } = (() => {
    const rd = album.releaseDate;
    if (!rd) return { isNew: false, isOutNow: false };
    try {
      const released = new Date(rd);
      if (Number.isNaN(released.getTime())) return { isNew: false, isOutNow: false };
      const ageDays = (Date.now() - released.getTime()) / (24 * 60 * 60 * 1000);
      return {
        isOutNow: ageDays >= 0 && ageDays <= 14,
        isNew: ageDays >= 0 && ageDays <= 60,
      };
    } catch {
      return { isNew: false, isOutNow: false };
    }
  })();

  return (
    <Link
      to={`/album/${album.collectionId}`}
      data-testid={`album-card-${album.collectionId}`}
      className="group flex flex-col gap-3 p-3 rounded-xl bg-white/[0.02] hover:bg-white/[0.06] border border-transparent hover:border-white/5 transition-all"
    >
      <div className="relative w-full aspect-square rounded-md overflow-hidden shadow-lg">
        <SmartImage
          src={mediumArt(album.artworkUrl100)}
          alt={album.collectionName || ""}
          seed={album.collectionName || album.artistName || "?"}
          className="absolute inset-0 w-full h-full"
          imgClassName="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          tileStyle={{ borderRadius: 0 }}
        />
        <div className="absolute top-2 left-2">
          <FireBadge item={album} size="md" />
        </div>
        {/* OUT NOW takes priority over NEW — both shown together would be
            visual noise. OUT NOW is louder (pulse animation) because the
            ≤14-day window means it's actively being promoted. */}
        {isOutNow ? (
          <span
            data-testid={`album-out-now-badge-${album.collectionId}`}
            className="absolute top-2 right-2 text-[9px] font-black uppercase tracking-[0.18em] px-2 py-0.5 rounded-sm bg-vermilion text-white shadow-[0_4px_14px_-2px_rgba(232,69,37,0.7)] animate-pulse"
          >
            Out Now
          </span>
        ) : isNew ? (
          <span
            data-testid={`album-new-badge-${album.collectionId}`}
            className="absolute top-2 right-2 text-[9px] font-black uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-sm bg-vermilion text-white shadow-[0_4px_14px_-2px_rgba(232,69,37,0.6)]"
          >
            New
          </span>
        ) : null}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium truncate">{album.collectionName}</p>
        <p className="text-xs text-white/50 truncate mt-0.5">{album.artistName}</p>
      </div>
    </Link>
  );
};

export const ArtistCard = ({ artist }) => {
  const [pic, setPic] = useState(artist.artworkUrl100 || null);
  const themeKey = artist.artistName || artist.artistId || "anon";
  const gradient = gradientCssForArtist(themeKey);

  useEffect(() => {
    if (pic) return;
    if (!artist.artistName) return;
    let cancelled = false;
    api
      .get("/artwork/artist", {
        params: { artist_name: artist.artistName, artist_id: artist.artistId },
      })
      .then((r) => {
        if (!cancelled && r.data?.picture) setPic(r.data.picture);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [artist.artistName, artist.artistId, pic]);

  // Brutalist vertical poster — replaces the generic round avatar. The card
  // is a vertical 3:4 rectangle (movie-poster proportions). Image is grayscale
  // by default and animates to full color on hover — feels editorial and
  // signals discovery rather than a "search result".
  return (
    <Link
      to={`/artist/${artist.artistId}`}
      data-testid={`artist-card-${artist.artistId}`}
      className="group flex flex-col flex-shrink-0 transition-transform hover:-translate-y-1 duration-300"
    >
      <div
        className="relative aspect-[3/4] overflow-hidden bg-[var(--obsidian-elevated)] flex items-end justify-center"
        style={pic ? undefined : { background: gradient }}
      >
        {pic ? (
          <img
            src={pic}
            alt={artist.artistName}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
          />
        ) : (
          <span className="font-display text-[120px] text-white drop-shadow-lg pointer-events-none select-none leading-none -mb-4">
            {artist.artistName?.[0] || "?"}
          </span>
        )}
        {/* Bottom gradient for text legibility */}
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black via-black/70 to-transparent pointer-events-none" />
        {/* Trending badge top-right */}
        <div className="absolute top-2 right-2">
          <FireBadge item={artist} size="sm" />
        </div>
        {/* Artist name absolutely positioned over the image */}
        <div className="absolute inset-x-0 bottom-0 p-3 z-10">
          <p className="font-display font-black text-white text-lg leading-tight uppercase tracking-tight truncate">
            {artist.artistName}
          </p>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#FF2E00] font-black mt-1">
            {artist.primaryGenreName || "Artist"}
          </p>
        </div>
      </div>
    </Link>
  );
};
